import { sessionListSearchValueAtom } from '@/atoms/sideNavAtoms';
import { useIsNewSessionListEnabled } from '@/features/flags';
import { restoreFirstSessionRequestAtom } from '@/features/sessions/context/sessions-panel-atoms';
import { filterAndSortSessions } from '@/helpers/sessionSortHelpers';
import { useQueryCombinedSessions } from '@/hooks/query/useQueryCombinedSessions';
import { useDebouncedNewSession } from '@/hooks/useDebouncedNewSession';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { selectedSessionIdsAtom } from '@heidi/session';
import { useQuerySessionById } from '@heidi/session/data';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useDebouncedCallback } from 'use-debounce';

export const useSessionDeletedRecovery = () => {
  const router = useRouter();
  const currentSessionId = useCurrentSessionId();
  const setSelectedSessionIds = useSetAtom(selectedSessionIdsAtom);
  const setSessionListSearchValue = useSetAtom(sessionListSearchValueAtom);
  const setRestoreFirstSessionRequest = useSetAtom(restoreFirstSessionRequestAtom);
  const { debouncedNewSessionClick } = useDebouncedNewSession();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  // `sessions` is only read by the flag-off redirect path (`firstSession` below);
  // `useQueryCombinedSessions` gates itself off on the new list, so it returns no
  // sessions and fires no legacy `/sessions` request there.
  const { sessions } = useQueryCombinedSessions();

  // Pick the most recent past session so the user doesn't get dropped onto a
  // future-dated / scheduled session after a delete (SCR-3475).
  const firstPastSessionId = sessions
    ? filterAndSortSessions(false, sessions)?.at(0)?.session_id
    : undefined;

  const { data: firstSession } = useQuerySessionById({
    sessionId: firstPastSessionId ?? '',
    select: (data) => ({
      session_id: data?.session_id,
    }),
  });

  return useDebouncedCallback((deletedSessionIds?: string[]) => {
    setSelectedSessionIds([]);
    setSessionListSearchValue('');

    // Only redirect when the session the user is currently viewing was part of
    // the delete. When no IDs are supplied (e.g. "delete empty sessions"), we
    // don't know what was deleted, so fall back to the legacy redirect.
    const currentSessionWasDeleted =
      deletedSessionIds === undefined ||
      (currentSessionId !== '' && deletedSessionIds.includes(currentSessionId));

    if (!currentSessionWasDeleted) {
      return;
    }

    // New session list: drop to the list and signal it to re-open the first
    // remaining session, excluding the just-deleted ids. With no ids (e.g.
    // "delete empty sessions") there's nothing to exclude, so just drop back.
    if (isNewSessionListEnabled) {
      if (deletedSessionIds) {
        setRestoreFirstSessionRequest((prev) => ({
          nonce: prev.nonce + 1,
          excludeSessionIds: deletedSessionIds,
        }));
      }
      void router.push('/scribe/sessions');
      return;
    }

    if (firstSession?.session_id) {
      void router.push(`/scribe/session/${firstSession.session_id}`);
      return;
    }
    debouncedNewSessionClick();
  }, 1);
};
